import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';
import { CacheService } from './cache.service';
import { LoginService } from './login.service';
import { ProjectService } from './project.service';
import { TokenEncryptionService } from './token-encryption.service';

export interface DemoPersona {
  id: string;
  name: string;
  /** Marketing persona size (may exceed seeded snapshot). */
  size: number;
  /** Employees actually present in the local demo database. */
  seededEmployeeCount: number;
  emphasis: string;
  liveAgents: number;
  companyAdminId: number;
  importedLocally: boolean;
}

const PERSONA_STORAGE_KEY = 'supersee_demo_persona';

@Injectable({
  providedIn: 'root',
})
export class DemoEnvironmentService {
  readonly personas: DemoPersona[] = [
    {
      id: 'smb-25',
      name: 'Acme Creative Co.',
      size: 25,
      seededEmployeeCount: 15,
      emphasis: 'Dashboard, teams, reports',
      liveAgents: 3,
      companyAdminId: 2547,
      importedLocally: true,
    },
    {
      id: 'reports-100',
      name: 'Nexus Operations',
      size: 100,
      seededEmployeeCount: 29,
      emphasis: 'Productivity trends, usage',
      liveAgents: 2,
      companyAdminId: 6580,
      importedLocally: true,
    },
    {
      id: 'screencast-50',
      name: 'BlueCast Services',
      size: 50,
      seededEmployeeCount: 46,
      emphasis: 'Live monitoring, screencast',
      liveAgents: 4,
      companyAdminId: 6005,
      importedLocally: true,
    },
  ];

  constructor(
    private loginService: LoginService,
    private authService: AuthService,
    private projectService: ProjectService,
    private cacheService: CacheService,
    private tokenEncryptionService: TokenEncryptionService,
  ) {}

  isDemoRealm(): boolean {
    return !!(environment as { demoRealm?: boolean }).demoRealm;
  }

  shouldSuppressTrialUx(): boolean {
    return this.isDemoRealm();
  }

  getDemoUserPassword(): string {
    return (environment as { demoUserPassword?: string }).demoUserPassword || 'Demo@SuperSee2026!';
  }

  getLoginUsername(personaId: string): string {
    return `demo_admin_${personaId}`;
  }

  getStoredPersonaId(): string | null {
    return localStorage.getItem(PERSONA_STORAGE_KEY);
  }

  getStoredPersona(): DemoPersona | null {
    const id = this.getStoredPersonaId();
    return id ? this.getPersonaById(id) ?? null : null;
  }

  getPersonaByCompanyAdminId(companyAdminId: number): DemoPersona | undefined {
    return this.personas.find((p) => p.companyAdminId === companyAdminId);
  }

  /** Resolve the persona for the current JWT session (source of truth). */
  getActivePersonaFromSession(): DemoPersona | null {
    const loggedIn = this.tokenEncryptionService.getEncryptedItem<{ company_id?: number | string }>(
      'logedInUser',
    );
    const companyId = Number(loggedIn?.company_id);
    if (Number.isFinite(companyId) && companyId > 0) {
      return this.getPersonaByCompanyAdminId(companyId) ?? null;
    }
    return this.getStoredPersona();
  }

  storePersonaId(personaId: string): void {
    localStorage.setItem(PERSONA_STORAGE_KEY, personaId);
  }

  getPersonaById(id: string): DemoPersona | undefined {
    return this.personas.find((p) => p.id === id);
  }

  switchToPersona(personaId: string, reloadAfterLogin = true): Observable<void> {
    const persona = this.getPersonaById(personaId);
    if (!persona) {
      return throwError(() => new Error(`Unknown persona: ${personaId}`));
    }
    if (!persona.importedLocally) {
      return throwError(
        () =>
          new Error(
            `"${persona.name}" is not in the local demo database yet. Import it with:\n` +
              `python scripts/import-to-demo-db.py --persona ${personaId} --full-pipeline --target local --yes`,
          ),
      );
    }

    this.storePersonaId(personaId);
    this.cacheService.clear();
    this.projectService.clearUserListCache();

    return this.loginService
      .doLogin({
        username: this.getLoginUsername(personaId),
        password: this.getDemoUserPassword(),
      })
      .pipe(
        tap((check: Record<string, unknown>) => {
          this.authService.handleSuccessfulLogin(check, false, false);
          if (reloadAfterLogin) {
            window.location.assign('/dashboard');
          }
        }),
        map(() => void 0),
      );
  }
}
